import { memo, useCallback, useRef } from 'react';
import { FlatList, ListRenderItem, View } from 'react-native';
import {
  List,
  PressableIcon,
  useBreakpointProp,
  useHover,
} from '@betty/moonlight';
import { useSelector } from '@betty/toolkit/state';
import { useListNavigation } from '@betty/navigation';

import useFilters from '../../hooks/useFilters';
import { selectCategoriesState } from '../../state';
import Filter from './Filter/Filter';
import FiltersSkeleton from './Skeleton/FiltersSkeleton';
import {
  CONTAINER_STYLE,
  LEFT_ARROW_CONTAINER_STYLE,
  RIGHT_ARROW_CONTAINER_STYLE,
} from './Filters.content';

function Filters() {
  const filters = useFilters();
  const dataState = useSelector(selectCategoriesState);
  const listRef = useRef<FlatList>(null);
  const containerRef = useRef<View>(null);

  const showArrows = useBreakpointProp({
    mobile: false,
    tablet: false,
    desktop: true,
  });

  const { isHovered, ...listHoverProps } = useHover();

  const validFilters = filters.filter(({ id, gamesCount }) => id || gamesCount);

  const {
    handleScroll,
    handleLayout,
    handleContentSizeChange,
    showLeftArrow,
    showRightArrow,
    handleArrowForward,
    handleArrowBack,
  } = useListNavigation({
    listRef,
    containerRef,
    itemWidth: undefined,
    showArrows,
    itemsCount: validFilters.length,
    enableDragging: true,
    enableArrows: true,
  });

  const renderFilter = useCallback<
    ListRenderItem<(typeof validFilters)[number]>
  >(({ item }) => <Filter {...item} />, []);

  const keyExtractor = useCallback(
    (item: (typeof validFilters)[number], index: number) =>
      `${item.id}-${index}`,
    [],
  );

  if (dataState === 'idle' || dataState === 'pending') {
    return <FiltersSkeleton />;
  }

  return (
    <View
      ref={containerRef}
      style={CONTAINER_STYLE}
      onLayout={handleLayout}
      {...listHoverProps}
    >
      <List
        ref={listRef}
        data={validFilters}
        keyExtractor={keyExtractor}
        renderItem={renderFilter}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        scrollEventThrottle={16}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 'space-300',
          paddingBottom: 'space-200',
        }}
      />
      {showArrows && showLeftArrow && (
        <View style={LEFT_ARROW_CONTAINER_STYLE}>
          <PressableIcon
            name="arrowBackOutlined"
            opacity={isHovered ? 0.9 : 0}
            alignItems="center"
            justifyContent="center"
            backgroundColor="overlayBackground"
            color="level_0"
            borderRadius="radius-600"
            iconHeight="space-500"
            iconWidth="space-500"
            width="space-800"
            height="space-800"
            onPress={handleArrowBack}
          />
        </View>
      )}
      {showArrows && showRightArrow && (
        <View style={RIGHT_ARROW_CONTAINER_STYLE}>
          <PressableIcon
            name="arrowForwardOutlined"
            opacity={isHovered ? 0.9 : 0}
            alignItems="center"
            justifyContent="center"
            backgroundColor="overlayBackground"
            color="level_0"
            borderRadius="radius-600"
            width="space-800"
            height="space-800"
            onPress={handleArrowForward}
          />
        </View>
      )}
    </View>
  );
}

export default memo(Filters);
